import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Download, Funnel, LucideAngularModule, Plus, ReceiptText, Search, ShieldCheck, Trash2, Upload, X } from 'lucide-angular';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BillVerification, Contract, Loa, LoaBillConfig, Vendor } from '../../../core/models/domain.models';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { DialogComponent } from '../../../shared/ui/dialog.component';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';

type BillTypeOption = 'OA' | 'SOP' | 'TMR';

interface BillVerificationPayload {
  seller_vendor_id?: string | null;
  loa_id: string;
  bill_type: BillTypeOption;
  invoice_number: string;
  invoice_date?: string | null;
  bill_date: string;
  bill_period_from?: string | null;
  bill_period_to?: string | null;
  billed_quantum?: number | null;
  submitted_rate?: number | null;
  submitted_amount?: number | null;
  remarks?: string | null;
}

interface BillEntryForm {
  billDate: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  billedQuantum: string | number;
  submittedRate: string | number;
  submittedAmount: string | number;
}

interface BillApprovalPreview {
  loa_id: string;
  bill_type: string;
  bill_cycle?: string | null;
  approved_quantum?: number | null;
  approved_amount?: number | null;
  expected_rate?: number | null;
  matched_applications: number;
  note?: string | null;
}

interface ExtractedInvoiceItem {
  sr_no?: string | number | null;
  description?: string | null;
  hsn_sac?: string | null;
  quantity?: string | number | null;
  unit?: string | null;
  amount?: number | string | null;
}

interface ExtractedInvoicePayload {
  invoice_type?: string | null;
  seller_details?: {
    seller_name?: string | null;
    seller_address?: string | null;
    seller_cin?: string | null;
    seller_email?: string | null;
    seller_state?: string | null;
    seller_gstin?: string | null;
  };
  buyer_details?: {
    buyer_name?: string | null;
    buyer_address?: string | null;
    buyer_state?: string | null;
    buyer_gstin?: string | null;
    buyer_pan?: string | null;
    place_of_supply?: string | null;
  };
  invoice_details?: {
    invoice_number?: string | null;
    invoice_date?: string | null;
    trade_date?: string | null;
    delivery_date?: string | null;
    exchange?: string | null;
    segment?: string | null;
  };
  item_details?: ExtractedInvoiceItem[];
  totals?: {
    total_amount?: number | string | null;
    amount_in_words?: string | null;
  };
  matched_context?: {
    seller_vendor_id?: string | null;
    seller_vendor_name?: string | null;
    buyer_vendor_id?: string | null;
    buyer_vendor_name?: string | null;
    contract_id?: string | null;
    contract_number?: string | null;
    loa_id?: string | null;
    loa_number?: string | null;
  };
  declaration?: string | null;
}

interface ApplicationScheduleApi {
  from_date: string;
  to_date?: string | null;
  from_time?: string | null;
  to_time?: string | null;
  quantum?: number | null;
}

interface ApplicationApprovalScheduleApi {
  schedule_from: string;
  schedule_to?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  mwh?: number | null;
  requested_mw?: number | null;
  accepted_mw?: number | null;
  is_approved?: boolean;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  seller_vendor_id?: string | null;
  seller_loa_id?: string | null;
  status: string;
  schedules?: ApplicationScheduleApi[];
  approval_schedules?: ApplicationApprovalScheduleApi[];
}

interface SellerBillGenerateRow {
  rowKey: string;
  applicationId: string;
  applicationNumber: string;
  applicationNumberTooltip: string;
  invoiceNumber: string | null;
  sellerVendorId: string;
  sellerName: string;
  loaId: string;
  loaNumber: string;
  billType: BillTypeOption;
  billCycle: string;
  billDate: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  totalQuantum: number;
  totalAmount: number | null;
  expectedAmount: number | null;
  billedQuantum: number | null;
  configuredRateLabel: string;
  submittedRate: number | null;
  submittedAmount: number | null;
  verificationId: string | null;
  verificationStatus: string;
  verificationNotes: string[];
  approvalStatus: string | null;
  approvalReason: string | null;
  approvalRole: string | null;
  approvalAt: string | null;
  statusTooltip: string | null;
}

type BillApprovalAction = 'approve' | 'reject' | 'force-approve';

interface SellerBillGenerateGroup {
  groupKey: string;
  sellerVendorId: string;
  sellerName: string;
  loaId: string;
  loaNumber: string;
  billCycle: string;
  bucketKey: string;
  bucketLabel: string;
  bucketFrom: string;
  bucketTo: string;
  rows: SellerBillGenerateRow[];
}

interface HeroStatItem {
  label: string;
  value: string;
  segments: HeroStatSegment[];
}

interface HeroStatSegment {
  label: string;
  value: number;
  tone: 'danger' | 'success' | 'info' | 'muted';
}

interface ApprovalDialogSummary {
  invoiceNumber: string;
  loaNumber: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  quantum: number | null;
  rate: number | null;
  amount: number | null;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, DialogComponent],
  templateUrl: './bill-verification.page.html',
  styleUrl: './bill-verification.page.scss',
})
export class BillVerificationPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly vendorsService = inject(VendorsService);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly toast = inject(ToastService);
  @ViewChild('invoiceFormSection') private invoiceFormSection?: ElementRef<HTMLElement>;

  protected readonly ReceiptTextIcon = ReceiptText;
  protected readonly UploadIcon = Upload;
  protected readonly DownloadIcon = Download;
  protected readonly FilterIcon = Funnel;
  protected readonly PlusIcon = Plus;
  protected readonly ShieldCheckIcon = ShieldCheck;
  protected readonly SearchIcon = Search;
  protected readonly TrashIcon = Trash2;
  protected readonly CloseIcon = X;

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly fetchingApproval = signal(false);
  protected readonly extractingInvoice = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly showForm = signal(false);
  protected readonly showSellerBillFilters = signal(false);
  protected readonly approvalActionLoadingId = signal<string | null>(null);
  protected readonly approvalReasonDialogOpen = signal(false);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly loas = signal<Loa[]>([]);
  protected readonly verifications = signal<BillVerification[]>([]);
  protected readonly applications = signal<ApplicationApi[]>([]);
  private readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  protected readonly billEntries = signal<BillEntryForm[]>([]);
  protected readonly search = signal('');
  protected readonly clientFilter = signal('');
  protected readonly loaFilter = signal('');
  protected readonly verificationPage = signal(1);
  protected readonly verificationPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly verificationPageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);
  protected readonly generateClientFilter = signal('');
  protected readonly generateLoaFilter = signal('');
  protected readonly generateSearch = signal('');
  protected readonly generateBillCycleFilter = signal('');
  protected readonly generateBillDateFrom = signal('');
  protected readonly generateBillDateTo = signal('');
  protected readonly sellerBillsPage = signal(1);
  protected readonly sellerBillsPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly sellerBillsPageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);

  protected sellerVendorId = '';
  protected buyerVendorId = '';
  protected contractId = '';
  protected loaId = '';
  protected generateClientFilterValue = '';
  protected generateLoaFilterValue = '';
  protected generateSearchValue = '';
  protected generateBillCycleFilterValue = '';
  protected generateBillDateFromValue = '';
  protected generateBillDateToValue = '';
  protected billType: BillTypeOption = 'SOP';
  protected invoiceNumber = '';
  protected invoiceDate = '';
  protected billDate = '';
  protected billPeriodFrom = '';
  protected billPeriodTo = '';
  protected billedQuantum: string | number = '';
  protected submittedRate: string | number = '';
  protected submittedAmount: string | number = '';
  protected remarks = '';
  protected selectedFile: File | null = null;
  protected approvalReason = '';
  protected pendingApprovalAction: BillApprovalAction | null = null;
  protected pendingApprovalVerificationId: string | null = null;
  protected pendingApprovalInvoiceNumber = '';
  protected pendingApprovalSummary: ApprovalDialogSummary | null = null;

  protected readonly isVendor = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly visibleRows = computed(() => {
    const term = this.search().trim().toLowerCase();
    const clientId = this.clientFilter().trim();
    const loaId = this.loaFilter().trim();
    return this.verifications().filter((row) => {
      if (clientId && row.seller_vendor_id !== clientId) return false;
      if (loaId && row.loa_id !== loaId) return false;
      if (!term) return true;
      return [
        row.seller_name,
        row.loa_number,
        row.bill_type,
        row.invoice_number,
        row.verification_status,
        ...(row.verification_notes || []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  });
  protected readonly verificationTotalPages = computed(() => Math.max(1, Math.ceil(this.visibleRows().length / this.verificationPageSize())));
  protected readonly verificationCurrentPage = computed(() => Math.min(this.verificationPage(), this.verificationTotalPages()));
  protected readonly verificationPageStartIndex = computed(() => (this.verificationCurrentPage() - 1) * this.verificationPageSize());
  protected readonly paginatedVisibleRows = computed(() => {
    const start = this.verificationPageStartIndex();
    return this.visibleRows().slice(start, start + this.verificationPageSize());
  });
  protected readonly verificationStartRow = computed(() => (this.visibleRows().length ? this.verificationPageStartIndex() + 1 : 0));
  protected readonly verificationEndRow = computed(() =>
    Math.min(this.verificationPageStartIndex() + this.verificationPageSize(), this.visibleRows().length),
  );
  protected readonly sellerBillsToGenerate = computed(() => {
    const loasById = new Map(this.loas().map((row) => [String(row.id), row]));
    const verifications = this.verifications();

    const appsByLoa = new Map<string, ApplicationApi[]>();
    this.applications()
      .filter((app) => (app.status || '').trim().toLowerCase() === 'punched')
      .filter((app) => this.applicationHasApprovedSchedule(app))
      .forEach((app) => {
        const sellerVendorId = String(app.seller_vendor_id || '').trim();
        const loaId = String(app.seller_loa_id || '').trim();
        if (!sellerVendorId || !loaId) return;
        if (this.isVendor() && sellerVendorId !== this.sellerVendorId) return;
        const list = appsByLoa.get(loaId) || [];
        list.push(app);
        appsByLoa.set(loaId, list);
      });

    const rows: SellerBillGenerateRow[] = [];
    for (const [loaId, apps] of appsByLoa.entries()) {
      const loa = loasById.get(loaId);
      const sellerVendorId = String(loa?.vendor_id || apps[0]?.seller_vendor_id || '').trim();
      if (!sellerVendorId) continue;

      const bounds = this.loaScheduleBounds(apps);
      if (!bounds) continue;

      const appsWithBounds = apps
        .map((app) => {
          const range = this.applicationDateBounds(app);
          if (!range) return null;
          const applicationNumber = String(app.application_number || app.id || '').trim();
          return applicationNumber
            ? { applicationNumber, start: range.start, end: range.end }
            : null;
        })
        .filter((item): item is { applicationNumber: string; start: Date; end: Date } => item !== null);

      const configs = this.supportedBillConfigs(this.billConfigsByLoa()[loaId] || []);
      configs.forEach((config) => {
        const billType = ((config.bill_type || '').trim().toUpperCase() || 'SOP') as BillTypeOption;
        const billCycle = (config.bill_cycle || '').trim() || 'NA';
        const periods = this.billPeriodsForCycle(bounds.start, bounds.end, billCycle);

        periods.forEach((period) => {
          const overlappingApps = apps
            .map((app) => ({ app, bounds: this.applicationDateBounds(app) }))
            .filter(
              (
                item,
              ): item is {
                app: ApplicationApi;
                bounds: { start: Date; end: Date };
              } => !!item.bounds && this.rangesOverlap(period.from, period.to, item.bounds.start, item.bounds.end),
            );

          if (!overlappingApps.length) return;

          const applicationNumbersForPeriod = this.applicationComboLabel(
            [...new Set(overlappingApps.map((item) => String(item.app.application_number || item.app.id || '').trim()).filter(Boolean))].sort((left, right) =>
              left.localeCompare(right),
            ),
          );

          const billPeriodFrom = this.formatIsoDate(period.from);
          const billPeriodTo = this.formatIsoDate(period.to);
          const billDate = this.formatIsoDate(period.billDate);
          const totalQuantum = Number(
            overlappingApps.reduce((sum, item) => sum + this.applicationQuantumForRange(item.app, period.from, period.to), 0).toFixed(4),
          );
          if (totalQuantum <= 0) return;

          const configuredRate = this.billRateValue(config);
          const derivedTotalAmount =
            configuredRate !== null
              ? Number(this.applicationAmountForRange(overlappingApps.map((item) => item.app), period.from, period.to, configuredRate).toFixed(2))
              : null;
          const existing = this.matchingVerification(verifications, loaId, billType, billCycle, period.from, period.to, totalQuantum);

          rows.push({
            rowKey: `${loaId}::${billType}::${billPeriodFrom}::${billPeriodTo}`,
            applicationId: '',
            applicationNumber: applicationNumbersForPeriod.label,
            applicationNumberTooltip: applicationNumbersForPeriod.tooltip,
            invoiceNumber: existing?.invoice_number || null,
            sellerVendorId,
            sellerName: this.sellerName(sellerVendorId),
            loaId,
            loaNumber: loa?.loa_number || loaId,
            billType,
            billCycle,
            billDate,
            billPeriodFrom,
            billPeriodTo,
            totalQuantum,
            totalAmount: derivedTotalAmount,
            expectedAmount: derivedTotalAmount ?? existing?.expected_amount ?? null,
            billedQuantum: existing?.billed_quantum ?? null,
            configuredRateLabel: this.billRateLabel(config),
            submittedRate: existing?.submitted_rate ?? null,
            submittedAmount: existing?.submitted_amount ?? null,
            verificationId: existing?.id || null,
            verificationStatus: existing?.verification_status || 'Pending Verification',
            verificationNotes: existing?.verification_notes || [],
            approvalStatus: existing?.approval_status || null,
            approvalReason: existing?.approval_reason || null,
            approvalRole: existing?.approval_role || null,
            approvalAt: existing?.approval_at || null,
            statusTooltip: this.sellerBillStatusTooltip({
              rowKey: `${loaId}::${billType}::${billPeriodFrom}::${billPeriodTo}`,
              applicationId: '',
              applicationNumber: applicationNumbersForPeriod.label,
              applicationNumberTooltip: applicationNumbersForPeriod.tooltip,
              invoiceNumber: existing?.invoice_number || null,
              sellerVendorId,
              sellerName: this.sellerName(sellerVendorId),
              loaId,
              loaNumber: loa?.loa_number || loaId,
              billType,
              billCycle,
              billDate,
              billPeriodFrom,
              billPeriodTo,
              totalQuantum,
              totalAmount: derivedTotalAmount,
              expectedAmount: derivedTotalAmount ?? existing?.expected_amount ?? null,
              billedQuantum: existing?.billed_quantum ?? null,
              configuredRateLabel: this.billRateLabel(config),
              submittedRate: existing?.submitted_rate ?? null,
              submittedAmount: existing?.submitted_amount ?? null,
              verificationId: existing?.id || null,
              verificationStatus: existing?.verification_status || 'Pending Verification',
              verificationNotes: existing?.verification_notes || [],
              approvalStatus: existing?.approval_status || null,
              approvalReason: existing?.approval_reason || null,
              approvalRole: existing?.approval_role || null,
              approvalAt: existing?.approval_at || null,
              statusTooltip: null,
            }),
          } satisfies SellerBillGenerateRow);
        });
      });
    }

    return rows.sort((left, right) => {
      if (left.sellerName !== right.sellerName) return left.sellerName.localeCompare(right.sellerName);
      if (left.loaNumber !== right.loaNumber) return left.loaNumber.localeCompare(right.loaNumber);
      if (left.billPeriodFrom !== right.billPeriodFrom) return right.billPeriodFrom.localeCompare(left.billPeriodFrom);
      return left.billType.localeCompare(right.billType);
    });
  });

  protected readonly sellerBillGroups = computed<SellerBillGenerateGroup[]>(() => {
    const groups = new Map<string, SellerBillGenerateGroup>();

    this.sellerBillsToGenerate().forEach((row) => {
      const bucket = this.billCycleBucket(row.billCycle, row.billDate || row.billPeriodTo || row.billPeriodFrom);
      const groupKey = `${row.loaId}::${(row.billCycle || '').trim().toLowerCase()}::${bucket.bucketKey}`;

      const existing = groups.get(groupKey);
      if (existing) {
        existing.rows.push(row);
        return;
      }

      groups.set(groupKey, {
        groupKey,
        sellerVendorId: row.sellerVendorId,
        sellerName: row.sellerName,
        loaId: row.loaId,
        loaNumber: row.loaNumber,
        billCycle: row.billCycle,
        bucketKey: bucket.bucketKey,
        bucketLabel: bucket.bucketLabel,
        bucketFrom: bucket.bucketFrom,
        bucketTo: bucket.bucketTo,
        rows: [row],
      });
    });

    const sortedGroups = [...groups.values()].sort((left, right) => {
      if (left.sellerName !== right.sellerName) return left.sellerName.localeCompare(right.sellerName);
      if (left.loaNumber !== right.loaNumber) return left.loaNumber.localeCompare(right.loaNumber);
      if (left.bucketFrom !== right.bucketFrom) return right.bucketFrom.localeCompare(left.bucketFrom);
      return left.billCycle.localeCompare(right.billCycle);
    });

    sortedGroups.forEach((group) => {
      group.rows.sort((left, right) => {
        if (left.billType !== right.billType) return left.billType.localeCompare(right.billType);
        if (left.billDate !== right.billDate) return right.billDate.localeCompare(left.billDate);
        return left.applicationNumber.localeCompare(right.applicationNumber);
      });
    });

    return sortedGroups;
  });
  protected readonly filteredSellerBillGroups = computed<SellerBillGenerateGroup[]>(() => {
    const clientId = this.generateClientFilter().trim();
    const loaId = this.generateLoaFilter().trim();
    const cycle = this.generateBillCycleFilter().trim().toLowerCase();
    const term = this.generateSearch().trim().toLowerCase();
    const dateFrom = this.generateBillDateFrom().trim();
    const dateTo = this.generateBillDateTo().trim();

    return this.sellerBillGroups().filter((group) => {
      if (clientId && group.sellerVendorId !== clientId) return false;
      if (loaId && group.loaId !== loaId) return false;
      if (cycle && group.billCycle.trim().toLowerCase() !== cycle) return false;
      if (term) {
        const matches = group.rows.some((row) =>
          [
            row.applicationNumber,
            row.invoiceNumber,
            row.sellerName,
            row.loaNumber,
            row.billType,
            row.billCycle,
            row.verificationStatus,
            row.approvalStatus,
          ]
            .join(' ')
            .toLowerCase()
            .includes(term),
        );
        if (!matches) return false;
      }
      if (dateFrom || dateTo) {
        const matchesDate = group.rows.some((row) => {
          const billDate = (row.billDate || '').trim();
          if (!billDate) return false;
          if (dateFrom && billDate < dateFrom) return false;
          if (dateTo && billDate > dateTo) return false;
          return true;
        });
        if (!matchesDate) return false;
      }
      return true;
    });
  });
  protected readonly filteredSellerBillRows = computed<SellerBillGenerateRow[]>(() =>
    this.filteredSellerBillGroups().flatMap((group) => group.rows),
  );
  protected readonly paginatedSellerBillRows = computed<SellerBillGenerateRow[]>(() => {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    const pageSize = this.sellerBillsPageSize();
    const start = (page - 1) * pageSize;
    return this.filteredSellerBillRows().slice(start, start + pageSize);
  });
  protected readonly sellerBillTotalPages = computed<number>(() => {
    const totalRows = this.filteredSellerBillRows().length;
    return Math.max(1, Math.ceil(totalRows / this.sellerBillsPageSize()));
  });
  protected readonly sellerBillCycleOptions = computed<string[]>(() =>
    Array.from(new Set(this.sellerBillsToGenerate().map((row) => row.billCycle).filter((value) => !!value))).sort((left, right) =>
      left.localeCompare(right),
    ),
  );
  protected readonly heroStats = computed<HeroStatItem[]>(() => {
    const rows = this.sellerBillsToGenerate();
    const verifications = this.verifications();
    const verifiedCount = rows.filter((row) => this.isVerifiedStatus(row.verificationStatus)).length;
    const attentionCount = rows.filter((row) => this.needsAttentionStatus(row.verificationStatus)).length;
    const pendingCount = rows.filter((row) => this.isPendingStatus(row.verificationStatus)).length;
    const uniqueSellers = new Set(rows.map((row) => row.sellerVendorId).filter(Boolean)).size;
    const uniqueLoas = new Set(rows.map((row) => row.loaId).filter(Boolean)).size;
    const submittedAmountTotal = verifications.reduce((sum, row) => sum + this.safeNumber(row.submitted_amount), 0);
    const submittedAmountCount = verifications.reduce(
      (count, row) => count + (row.submitted_amount !== null && row.submitted_amount !== undefined && row.submitted_amount !== null ? 1 : 0),
      0,
    );
    const pendingAmountTotal = rows
      .filter((row) => this.isPendingStatus(row.verificationStatus))
      .reduce((sum, row) => sum + this.safeNumber(row.expectedAmount), 0);
    const expectedAmountTotal = rows.reduce((sum, row) => sum + this.safeNumber(row.expectedAmount), 0);

    if (this.isVendor()) {
      return [
        {
          label: 'Total Bills',
          value: this.formatStatNumber(rows.length),
          segments: [
            { label: 'Attention', value: attentionCount, tone: 'danger' },
            { label: 'Pending', value: pendingCount, tone: 'info' },
            { label: 'Verified', value: verifiedCount, tone: 'success' },
          ],
        },
        {
          label: 'Verified',
          value: this.formatStatNumber(verifiedCount),
          segments: [
            { label: 'Verified', value: verifiedCount, tone: 'danger' },
            { label: 'Others', value: Math.max(rows.length - verifiedCount, 0), tone: 'info' },
          ],
        },
        {
          label: 'Pending Bills',
          value: this.formatStatNumber(pendingCount),
          segments: [
            { label: 'Pending', value: pendingCount, tone: 'danger' },
            { label: 'Attention', value: attentionCount, tone: 'info' },
          ],
        },
        {
          label: 'Invoice value',
          value: this.formatCurrencyStat(submittedAmountTotal || expectedAmountTotal),
          segments: [{ label: 'Amount', value: 1, tone: 'muted' }],
        },
      ];
    }

    return [
      {
        label: 'Total checks',
        value: this.formatStatNumber(verifications.length),
        segments: [
          { label: 'Verified', value: verifiedCount, tone: 'danger' },
          { label: 'Pending', value: pendingCount, tone: 'info' },
          { label: 'Attention', value: attentionCount, tone: 'success' },
        ],
      },
      {
        label: 'Active sellers',
        value: this.formatStatNumber(uniqueSellers),
        segments: [{ label: 'Sellers', value: uniqueSellers, tone: uniqueSellers > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'LOAs covered',
        value: this.formatStatNumber(uniqueLoas),
        segments: [{ label: 'LOAs', value: uniqueLoas, tone: uniqueLoas > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'Pending bills',
        value: this.formatStatNumber(pendingCount),
        segments: [
          { label: 'Pending', value: pendingCount, tone: 'danger' },
          { label: 'Attention', value: attentionCount, tone: 'info' },
        ],
      },
      {
        label: 'Submitted Amount',
        value: this.formatCurrencyStat(submittedAmountTotal),
        segments: [{ label: 'Bills', value: submittedAmountCount, tone: submittedAmountCount > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'Pending Amount',
        value: this.formatCurrencyStat(pendingAmountTotal),
        segments: [{ label: 'Bills', value: pendingCount, tone: pendingCount > 0 ? 'danger' : 'muted' }],
      },
    ];
  });
  ngOnInit(): void {
    if (this.isVendor()) {
      this.sellerVendorId = this.auth.user()?.vendor_id || '';
    }
    this.loadData();
  }

  protected availableLoas(): Loa[] {
    const sellerId = this.sellerVendorId;
    if (!sellerId) return [];
    const sellerLoas = this.loas().filter((row) => row.vendor_id === sellerId && this.resolveLoaKind(row) === 'sell');
    if (!this.contractId) return sellerLoas;

    const allowedLoaIds = new Set(this.contractSellerLoaIds(this.selectedContract()));
    return sellerLoas.filter((row) => allowedLoaIds.has(String(row.id)));
  }

  protected availableBuyers(): Vendor[] {
    const sellerId = this.sellerVendorId.trim();
    if (!sellerId) return [];

    const buyerIds = new Set(
      this.contracts()
        .filter((contract) => this.contractIncludesVendor(contract, sellerId, 'seller'))
        .flatMap((contract) => this.contractPartyVendorIds(contract, 'buyer')),
    );

    const buyerOptions = this.vendors().filter((vendor) => vendor.id !== sellerId && buyerIds.has(String(vendor.id)));
    return buyerOptions;
  }

  protected availableContracts(): Contract[] {
    const sellerId = this.sellerVendorId.trim();
    const buyerId = this.buyerVendorId.trim();
    return this.contracts().filter((contract) => {
      if (sellerId && !this.contractIncludesVendor(contract, sellerId, 'seller')) return false;
      if (buyerId && !this.contractIncludesVendor(contract, buyerId, 'buyer')) return false;
      return true;
    });
  }

  protected filterLoas(): Loa[] {
    const clientId = this.clientFilter().trim();
    if (!clientId) return this.loas();
    return this.loas().filter((row) => row.vendor_id === clientId);
  }

  protected generateFilterLoas(): Loa[] {
    const clientId = this.generateClientFilterValue.trim();
    if (!clientId) return this.loas();
    return this.loas().filter((row) => row.vendor_id === clientId);
  }

  protected sellerName(vendorId: string): string {
    return this.vendors().find((row) => row.id === vendorId)?.vendor_name || '-';
  }

  protected submit(): void {
    if (!this.loaId || !this.invoiceNumber.trim()) {
      this.toast.show('Missing details', 'Seller LOA and invoice number are required.', 'danger');
      return;
    }
    if (!this.editingId() && !this.billEntries()[0]?.billDate) {
      this.toast.show('Missing bill date', 'Bill date is required.', 'danger');
      return;
    }
    if (this.editingId() && !this.billDate) {
      this.toast.show('Missing details', 'Bill date is required.', 'danger');
      return;
    }

    const payload = this.editingId() ? this.buildEditFormData() : this.buildCreateFormData();
    const request$: Observable<BillVerification | BillVerification[] | null> = this.editingId()
      ? this.http.patch<BillVerification>(this.api.resolve(`/api/bill-verifications/${this.editingId()}`), payload, { withCredentials: true })
      : this.http.post<BillVerification[]>(this.api.resolve('/api/bill-verifications'), payload, { withCredentials: true });
    this.saving.set(true);
    request$
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const message = typeof error.error?.detail === 'string' && error.error.detail.trim()
            ? error.error.detail.trim()
            : 'Bill verification could not be completed.';
          this.toast.show('Submission failed', message, 'danger');
          return of(null);
        }),
      )
      .subscribe((response: BillVerification | BillVerification[] | null) => {
        this.saving.set(false);
        if (!response) {
          return;
        }
        const rows = Array.isArray(response) ? response : [response];
        this.verifications.update((items) => {
          const ids = new Set(rows.map((row) => row.id));
          const filtered = items.filter((item) => !ids.has(item.id));
          return [...rows, ...filtered];
        });
        const first = rows[0];
        this.toast.show(
          this.editingId() ? 'Bill updated' : 'Bills verified',
          this.editingId()
            ? `${first.invoice_number} marked as ${first.verification_status}.`
            : `${rows.length} bill line(s) saved for invoice ${first.invoice_number}.`,
          'success',
        );
        this.resetFormAfterSubmit();
      });
  }

  protected edit(row: BillVerification): void {
    this.showForm.set(true);
    this.scrollToInvoiceSection();
    this.editingId.set(row.id);
    this.sellerVendorId = row.seller_vendor_id;
    this.loaId = row.loa_id;
    this.billType = (row.bill_type as BillTypeOption) || 'SOP';
    this.invoiceNumber = row.invoice_number || '';
    this.invoiceDate = row.invoice_date || '';
    this.billDate = row.bill_date || '';
    this.billPeriodFrom = row.bill_period_from || '';
    this.billPeriodTo = row.bill_period_to || '';
    this.billedQuantum = row.billed_quantum ?? '';
    this.submittedRate = row.submitted_rate ?? '';
    this.submittedAmount = row.submitted_amount ?? '';
    this.remarks = row.remarks || '';
    this.selectedFile = null;
  }

  protected cancelEdit(): void {
    this.resetFormAfterSubmit();
  }

  protected openAddInvoice(): void {
    this.resetFormAfterSubmit();
    this.showForm.set(true);
    this.scrollToInvoiceSection();
  }

  protected useSellerBill(row: SellerBillGenerateRow): void {
    const existing = row.verificationId ? this.verifications().find((item) => item.id === row.verificationId) : null;
    if (existing) {
      this.edit(existing);
      return;
    }

    this.resetFormAfterSubmit();
    this.showForm.set(true);
    this.sellerVendorId = row.sellerVendorId;
    this.loaId = row.loaId;
    this.billType = row.billType;
    this.billEntries.set([
      {
        billDate: row.billDate,
        billPeriodFrom: row.billPeriodFrom,
        billPeriodTo: row.billPeriodTo,
        billedQuantum: '',
        submittedRate: '',
        submittedAmount: '',
      },
    ]);
    this.scrollToInvoiceSection();
  }

  protected closeForm(): void {
    this.resetFormAfterSubmit();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.selectedFile = input?.files?.[0] || null;
    if (!this.selectedFile) return;
    this.extractAndApplyInvoiceDetails(this.selectedFile);
  }

  protected downloadFile(row: BillVerification): void {
    if (!row.invoice_download_url) return;
    window.open(this.api.resolve(row.invoice_download_url), '_blank');
  }

  protected delete(row: BillVerification): void {
    const confirmed = window.confirm(`Delete invoice ${row.invoice_number}?`);
    if (!confirmed) return;

    this.http
      .delete(this.api.resolve(`/api/bill-verifications/${row.id}`), { withCredentials: true })
      .subscribe({
        next: () => {
          this.verifications.update((items) => items.filter((item) => item.id !== row.id));
          if (this.editingId() === row.id) {
            this.resetFormAfterSubmit();
          }
          this.toast.show('Deleted', `${row.invoice_number} removed successfully.`, 'success');
        },
        error: () => {
          this.toast.show('Delete failed', 'Bill verification could not be deleted.', 'danger');
        },
      });
  }

  protected canManageBillApproval(): boolean {
    const role = (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['admin', 'super_admin', 'accounts', 'account'].includes(role);
  }

  protected shouldShowReviewDecision(row: SellerBillGenerateRow): boolean {
    return !!row.verificationId && this.canManageBillApproval() && this.displayApprovalStatus(row) === 'Pending';
  }

  protected shouldShowOpenVerification(row: SellerBillGenerateRow): boolean {
    if (!row.verificationId) return true;
    return this.displayApprovalStatus(row) !== 'Approved';
  }

  protected shouldShowVerificationStatus(row: SellerBillGenerateRow): boolean {
    return this.displayApprovalStatus(row) !== 'Approved';
  }

  protected displayVerificationStatus(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'verified') return 'Matched';
    return (value || '').trim() || 'Pending';
  }

  protected displayApprovalStatus(row: SellerBillGenerateRow | BillVerification): string {
    const raw = ('approvalStatus' in row ? row.approvalStatus : row.approval_status) || null;
    const normalized = (raw || '').trim().toLowerCase();
    if (!normalized || normalized === 'pending approval') return 'Pending';
    if (normalized === 'approved') return 'Approved';
    if (normalized === 'force approved') return 'Force Approved';
    if (normalized === 'rejected') return 'Rejected';
    return (raw || '').trim() || 'Pending';
  }

  protected approvalStatusVariant(value: string | null | undefined): 'success' | 'warning' | 'danger' | 'secondary' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'approved') return 'success';
    if (normalized === 'force approved') return 'warning';
    if (normalized === 'rejected') return 'danger';
    return 'secondary';
  }

  protected openApprovalDecisionDialog(row: SellerBillGenerateRow): void {
    if (!row.verificationId) {
      this.toast.show('Verification required', 'Create the bill verification before taking an approval decision.', 'danger');
      return;
    }
    if (!this.canManageBillApproval()) {
      this.toast.show('Access denied', 'Only admin or accounts members can take bill approval actions.', 'danger');
      return;
    }
    this.pendingApprovalAction = this.defaultApprovalAction(row.approvalStatus);
    this.pendingApprovalVerificationId = row.verificationId;
    this.pendingApprovalInvoiceNumber = row.invoiceNumber || row.applicationNumber || row.loaNumber || 'this bill';
    this.pendingApprovalSummary = {
      invoiceNumber: row.invoiceNumber || '-',
      loaNumber: row.loaNumber || '-',
      billPeriodFrom: row.billPeriodFrom || '',
      billPeriodTo: row.billPeriodTo || '',
      quantum: row.billedQuantum ?? row.totalQuantum ?? null,
      rate: row.submittedRate ?? null,
      amount: row.submittedAmount ?? row.expectedAmount ?? row.totalAmount ?? null,
    };
    this.approvalReason = row.approvalReason || '';
    this.approvalReasonDialogOpen.set(true);
  }

  protected closeApprovalReasonDialog(): void {
    this.approvalReasonDialogOpen.set(false);
    this.pendingApprovalAction = null;
    this.pendingApprovalVerificationId = null;
    this.pendingApprovalInvoiceNumber = '';
    this.pendingApprovalSummary = null;
    this.approvalReason = '';
  }

  protected submitApprovalReason(): void {
    if (!this.pendingApprovalVerificationId || !this.pendingApprovalAction) return;
    const reason = this.approvalReason.trim();
    if (!reason) {
      this.toast.show('Reason required', 'Please enter a reason before continuing.', 'danger');
      return;
    }
    this.runApprovalAction(this.pendingApprovalVerificationId, this.pendingApprovalAction, reason || undefined);
  }

  protected approvalActionLabel(action: BillApprovalAction | null): string {
    if (action === 'approve') return 'Approve';
    if (action === 'reject') return 'Reject';
    if (action === 'force-approve') return 'Force Approve';
    return 'Select Action';
  }

  protected approvalActionDescription(action: BillApprovalAction): string {
    if (action === 'approve') return 'Use this when the bill is acceptable and can move ahead normally.';
    if (action === 'reject') return 'Use this when the bill should be sent back with a clear rejection remark.';
    return 'Use this when you want to override issues and approve with a mandatory justification.';
  }

  protected statusVariant(value: string): 'success' | 'warning' | 'danger' | 'secondary' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'verified') return 'success';
    if (normalized.includes('mismatch')) return 'danger';
    if (normalized.includes('outside')) return 'warning';
    if (normalized.includes('missing')) return 'warning';
    return 'secondary';
  }

  protected statusTooltip(row: BillVerification): string | null {
    const messages = [row.rate_error, row.amount_error].map((value) => (value || '').trim()).filter((value) => !!value);
    if ((row.approval_status || '').trim()) {
      messages.push(`Approval Status: ${row.approval_status}`);
    }
    if ((row.approval_reason || '').trim()) {
      messages.push(`Approval Reason: ${row.approval_reason}`);
    }
    return messages.length ? messages.join('\n') : null;
  }

  protected formatAmount(value: number | null | undefined): string {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }

  protected formatQuantum(value: number | null | undefined): string {
    const quantity = Number(value);
    if (!Number.isFinite(quantity)) return '-';
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(quantity);
  }

  protected statSegmentWidth(value: number, total: number): number {
    if (total <= 0) return 100;
    return (value / total) * 100;
  }

  protected statSegmentClass(tone: HeroStatSegment['tone']): string {
    return `summary-tile-segment ${tone}`;
  }

  protected statSegmentTotal(stat: HeroStatItem): number {
    const total = stat.segments.reduce((sum, item) => sum + item.value, 0);
    return total > 0 ? total : 1;
  }

  private formatStatNumber(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
  }

  private formatCurrencyStat(value: number): string {
    return this.formatAmount(value);
  }

  private safeNumber(value: number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isVerifiedStatus(value: string | null | undefined): boolean {
    return (value || '').trim().toLowerCase() === 'verified';
  }

  private isPendingStatus(value: string | null | undefined): boolean {
    return (value || '').trim().toLowerCase().includes('pending');
  }

  private needsAttentionStatus(value: string | null | undefined): boolean {
    const normalized = (value || '').trim().toLowerCase();
    return normalized.includes('mismatch') || normalized.includes('outside') || normalized.includes('missing');
  }

  private sellerBillStatusTooltip(row: SellerBillGenerateRow): string | null {
    const lines = [
      `Bill Date: ${this.formatDate(row.billDate)}`,
      `Bill Period From: ${this.formatDate(row.billPeriodFrom)}`,
      `Bill Period To: ${this.formatDate(row.billPeriodTo)}`,
      `Total Quantum: ${this.formatQuantum(row.totalQuantum)}`,
      `Billed Quantum: ${this.formatQuantum(row.billedQuantum)}`,
      `Configured Rate: ${row.configuredRateLabel || '-'}`,
      `Submitted Rate: ${row.submittedRate ?? '-'}`,
      `Expected Amount: ${this.formatAmount(row.expectedAmount)}`,
      `Submitted Amount: ${this.formatAmount(row.submittedAmount)}`,
      `Approval Status: ${this.displayApprovalStatus(row)}`,
    ];

    if (row.approvalReason) {
      lines.push(`Approval Reason: ${row.approvalReason}`);
    }

    if (row.verificationNotes.length) {
      lines.push(...row.verificationNotes);
    }

    return lines.filter((value) => !!String(value).trim()).join('\n');
  }

  protected formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const raw = value.trim();
    if (!raw) return '-';

    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}-${month}-${year}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  protected onSellerChange(): void {
    this.syncContractFilters();
  }

  protected onBuyerChange(): void {
    this.syncContractFilters();
  }

  protected onContractChange(value: string): void {
    this.contractId = value || '';
    const selectedLoaId = this.loaId.trim();
    if (!selectedLoaId) return;
    if (this.availableLoas().some((row) => String(row.id) === selectedLoaId)) return;
    this.loaId = '';
    this.toast.show('Only seller LOA can be selected', 'Select a seller LOA linked to the chosen contract.', 'info');
  }

  protected onLoaChange(value: string): void {
    const nextLoaId = (value || '').trim();
    if (!nextLoaId) {
      this.loaId = '';
      return;
    }

    const loa = this.loas().find((row) => String(row.id) === nextLoaId) || null;
    if (!loa || this.resolveLoaKind(loa) !== 'sell') {
      this.loaId = '';
      this.toast.show('Only seller LOA can be selected', 'Please choose a seller LOA only.', 'info');
      return;
    }

    if (!this.availableLoas().some((row) => String(row.id) === nextLoaId)) {
      this.loaId = '';
      this.toast.show('Only seller LOA can be selected', 'Select a seller LOA linked to the chosen contract.', 'info');
      return;
    }

    this.loaId = nextLoaId;
  }

  protected fetchApprovalValues(): void {
    const targetBillDate = this.editingId() ? this.billDate : this.billEntries()[0]?.billDate;
    const targetBillPeriodFrom = this.editingId() ? this.billPeriodFrom : this.billEntries()[0]?.billPeriodFrom;
    const targetBillPeriodTo = this.editingId() ? this.billPeriodTo : this.billEntries()[0]?.billPeriodTo;

    if (!this.loaId) {
      this.toast.show('Missing details', 'Select seller LOA before fetching approval values.', 'danger');
      return;
    }
    if (!targetBillDate) {
      this.toast.show('Missing bill date', 'Bill date is required to fetch approval quantum and amount.', 'danger');
      return;
    }

    const params = new URLSearchParams({
      loa_id: this.loaId,
      bill_type: this.billType,
      bill_date: targetBillDate,
    });
    if (this.sellerVendorId) params.set('seller_vendor_id', this.sellerVendorId);
    if (targetBillPeriodFrom) params.set('bill_period_from', targetBillPeriodFrom);
    if (targetBillPeriodTo) params.set('bill_period_to', targetBillPeriodTo);

    this.fetchingApproval.set(true);
    this.api
      .get<BillApprovalPreview>(`/api/bill-verifications/approval-preview?${params.toString()}`)
      .pipe(catchError(() => of(null)))
      .subscribe((preview) => {
        this.fetchingApproval.set(false);
        if (!preview) {
          this.toast.show('Fetch failed', 'Approval quantum and amount could not be fetched.', 'danger');
          return;
        }
        const derivedAmount = this.calculateSubmittedAmount(preview.approved_quantum, preview.expected_rate);
        const previewAmount = derivedAmount === '' ? (preview.approved_amount ?? '') : derivedAmount;

        if (this.editingId()) {
          this.billedQuantum = preview.approved_quantum ?? '';
          this.submittedAmount = previewAmount;
        } else {
          this.updateBillEntry(0, {
            billedQuantum: preview.approved_quantum ?? '',
            submittedAmount: previewAmount,
          });
        }

        this.toast.show(
          'Approval values fetched',
          preview.note || 'Seller LOA approval quantum and amount were loaded.',
          preview.approved_quantum || preview.approved_amount ? 'success' : 'info',
        );
      });
  }

  protected onClientFilterChange(): void {
    const loaId = this.loaFilter().trim();
    if (loaId && !this.filterLoas().some((row) => row.id === loaId)) {
      this.loaFilter.set('');
    }
    this.verificationPage.set(1);
  }

  protected onGenerateClientFilterChange(value: string): void {
    this.generateClientFilterValue = value;
    const loaId = this.generateLoaFilterValue.trim();
    if (loaId && !this.generateFilterLoas().some((row) => row.id === loaId)) {
      this.generateLoaFilterValue = '';
    }
  }

  protected onLoaFilterChange(value: string): void {
    this.loaFilter.set(value);
    this.verificationPage.set(1);
  }

  protected onVerificationSearchChange(value: string): void {
    this.search.set(value);
    this.verificationPage.set(1);
  }

  protected onGenerateLoaFilterChange(value: string): void {
    this.generateLoaFilterValue = value;
  }

  protected onGenerateSearchChange(value: string): void {
    this.generateSearchValue = value;
  }

  protected onGenerateBillDateFromChange(value: string): void {
    this.generateBillDateFromValue = value;
    if (this.generateBillDateToValue.trim() && value.trim() && this.generateBillDateToValue.trim() < value.trim()) {
      this.generateBillDateToValue = value;
    }
  }

  protected onGenerateBillDateToChange(value: string): void {
    this.generateBillDateToValue = value;
    if (this.generateBillDateFromValue.trim() && value.trim() && this.generateBillDateFromValue.trim() > value.trim()) {
      this.generateBillDateFromValue = value;
    }
  }

  protected onGenerateBillCycleFilterChange(value: string): void {
    this.generateBillCycleFilterValue = value;
  }

  protected applySellerBillFilters(): void {
    this.generateClientFilter.set(this.generateClientFilterValue.trim());
    this.generateLoaFilter.set(this.generateLoaFilterValue.trim());
    this.generateSearch.set(this.generateSearchValue.trim());
    this.generateBillCycleFilter.set(this.generateBillCycleFilterValue.trim());
    this.generateBillDateFrom.set(this.generateBillDateFromValue.trim());
    this.generateBillDateTo.set(this.generateBillDateToValue.trim());
    this.sellerBillsPage.set(1);
  }

  protected toggleSellerBillFilters(): void {
    this.showSellerBillFilters.update((value) => !value);
  }

  protected clearSellerBillFilters(): void {
    this.generateClientFilterValue = '';
    this.generateLoaFilterValue = '';
    this.generateSearchValue = '';
    this.generateBillCycleFilterValue = '';
    this.generateBillDateFromValue = '';
    this.generateBillDateToValue = '';
    this.generateClientFilter.set('');
    this.generateLoaFilter.set('');
    this.generateSearch.set('');
    this.generateBillCycleFilter.set('');
    this.generateBillDateFrom.set('');
    this.generateBillDateTo.set('');
    this.sellerBillsPage.set(1);
  }

  protected onSellerBillsPageSizeChange(value: string | number): void {
    const parsed = Number(value);
    const nextSize = this.sellerBillsPageSizeOptions.find((size) => size === parsed) ?? this.sellerBillsPageSizeOptions[0] ?? 10;
    this.sellerBillsPageSize.set(nextSize);
    this.sellerBillsPage.set(1);
  }

  protected sellerBillSerialNumber(index: number): number {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return (page - 1) * this.sellerBillsPageSize() + index + 1;
  }

  protected sellerBillsStartRow(): number {
    if (!this.filteredSellerBillRows().length) return 0;
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return (page - 1) * this.sellerBillsPageSize() + 1;
  }

  protected sellerBillsEndRow(): number {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return Math.min(page * this.sellerBillsPageSize(), this.filteredSellerBillRows().length);
  }

  protected sellerBillsCurrentPage(): number {
    return Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
  }

  protected goToSellerBillsPage(page: number): void {
    const totalPages = this.sellerBillTotalPages();
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    this.sellerBillsPage.set(nextPage);
  }

  protected onVerificationPageSizeChange(value: string | number): void {
    const parsed = Number(value);
    const nextSize = this.verificationPageSizeOptions.find((size) => size === parsed) ?? this.verificationPageSizeOptions[0] ?? 10;
    this.verificationPageSize.set(nextSize);
    this.verificationPage.set(1);
  }

  protected verificationSerialNumber(index: number): number {
    return this.verificationPageStartIndex() + index + 1;
  }

  protected goToVerificationPage(page: number): void {
    const nextPage = Math.min(Math.max(page, 1), this.verificationTotalPages());
    this.verificationPage.set(nextPage);
  }

  private runApprovalAction(verificationId: string, action: BillApprovalAction, reason?: string): void {
    const endpoint =
      action === 'approve'
        ? 'approve'
        : action === 'reject'
          ? 'reject'
          : 'force-approve';
    const titles: Record<BillApprovalAction, string> = {
      approve: 'Approved',
      reject: 'Rejected',
      'force-approve': 'Force approved',
    };

    this.approvalActionLoadingId.set(verificationId);
    this.api
      .post<BillVerification>(`/api/bill-verifications/${verificationId}/${endpoint}`, { reason: reason || null })
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        this.approvalActionLoadingId.set(null);
        if (!response) {
          this.toast.show('Action failed', 'Bill approval action could not be completed.', 'danger');
          return;
        }

        this.verifications.update((items) => items.map((item) => (item.id === response.id ? response : item)));
        if (this.editingId() === response.id) {
          this.remarks = response.remarks || this.remarks;
        }
        this.toast.show(titles[action], `${response.invoice_number} marked as ${response.approval_status || titles[action]}.`, 'success');
        this.closeApprovalReasonDialog();
      });
  }

  private defaultApprovalAction(approvalStatus: string | null | undefined): BillApprovalAction {
    const normalized = (approvalStatus || '').trim().toLowerCase();
    if (normalized === 'approved') return 'approve';
    if (normalized === 'rejected') return 'reject';
    if (normalized === 'force approved') return 'force-approve';
    return 'approve';
  }

  protected updateBillEntry(index: number, patch: Partial<BillEntryForm>): void {
    this.billEntries.update((items) =>
      items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const next = { ...item, ...patch };
        return {
          ...next,
          submittedAmount: this.calculateSubmittedAmount(next.billedQuantum, next.submittedRate),
        };
      }),
    );
  }

  protected updateEditBilledQuantum(value: string | number): void {
    this.billedQuantum = value;
    this.submittedAmount = this.calculateSubmittedAmount(this.billedQuantum, this.submittedRate);
  }

  protected updateEditSubmittedRate(value: string | number): void {
    this.submittedRate = value;
    this.submittedAmount = this.calculateSubmittedAmount(this.billedQuantum, this.submittedRate);
  }

  protected addBillEntry(): void {
    this.billEntries.update((items) => [...items, this.createEmptyBillEntry()]);
  }

  protected removeBillEntry(index: number): void {
    this.billEntries.update((items) => {
      if (items.length <= 1) return items;
      return items.filter((_, itemIndex) => itemIndex !== index);
    });
  }

  private loadData(): void {
    this.loading.set(true);
    forkJoin({
      vendors: this.vendorsService.list().pipe(
        catchError(() =>
          this.isVendor() && this.sellerVendorId
            ? this.vendorsService.get(this.sellerVendorId).pipe(
                map((vendor) => (vendor ? [vendor] : [])),
                catchError(() => of([] as Vendor[])),
              )
            : of([] as Vendor[]),
        ),
      ),
      contracts: this.contractsService.list().pipe(catchError(() => of([] as Contract[]))),
      loas: this.loaService.list().pipe(catchError(() => of([]))),
      verifications: this.api.get<BillVerification[]>('/api/bill-verifications').pipe(catchError(() => of([]))),
      applications: this.api.get<ApplicationApi[]>('/api/applications').pipe(catchError(() => of([]))),
    })
      .pipe(
        switchMap(({ vendors, contracts, loas, verifications, applications }) => {
          const loaIds = Array.from(
            new Set(
              (applications || [])
                .map((item) => String(item.seller_loa_id || '').trim())
                .filter((item) => !!item),
            ),
          );

          if (!loaIds.length) {
            return of({ vendors, contracts, loas, verifications, applications, configsByLoa: {} as Record<string, LoaBillConfig[]> });
          }

          return forkJoin(
            loaIds.map((loaId) =>
              this.loaService.listBillConfigs(loaId).pipe(
                catchError(() => of([] as LoaBillConfig[])),
                map((configs) => ({ loaId, configs })),
              ),
            ),
          ).pipe(
            map((configRows) => ({
              vendors,
              loas,
              contracts,
              verifications,
              applications,
              configsByLoa: configRows.reduce<Record<string, LoaBillConfig[]>>((acc, item) => {
                acc[item.loaId] = item.configs || [];
                return acc;
              }, {}),
            })),
          );
        }),
      )
      .subscribe(({ vendors, contracts, loas, verifications, applications, configsByLoa }) => {
        this.vendors.set((vendors || []).filter((row) => !!row?.id));
        this.contracts.set(contracts || []);
        this.loas.set(loas || []);
        this.verifications.set(verifications || []);
        this.applications.set(applications || []);
        this.billConfigsByLoa.set(configsByLoa || {});
        this.loading.set(false);
      });
  }

  private extractAndApplyInvoiceDetails(file: File): void {
    const formData = new FormData();
    formData.append('invoice_file', file);
    this.extractingInvoice.set(true);
    this.http
      .post<ExtractedInvoicePayload>(this.api.resolve('/api/bill-verifications/extract-invoice'), formData, { withCredentials: true })
      .pipe(
        catchError(() => {
          this.extractingInvoice.set(false);
          this.toast.show('Extraction failed', 'Could not read invoice details from the uploaded file.', 'danger');
          return of(null);
        }),
      )
      .subscribe((payload) => {
        this.extractingInvoice.set(false);
        if (!payload) return;
        this.applyExtractedInvoicePayload(payload);
      });
  }

  private applyExtractedInvoicePayload(payload: ExtractedInvoicePayload): void {
    const invoice = payload.invoice_details || {};
    const matchedContext = payload.matched_context || {};
    const sellerName = (payload.seller_details?.seller_name || '').trim();
    const sellerAddress = String(payload.seller_details?.seller_address || '').trim();
    const sellerCin = String(payload.seller_details?.seller_cin || '').trim();
    const sellerEmail = String(payload.seller_details?.seller_email || '').trim();
    const sellerState = String(payload.seller_details?.seller_state || '').trim();
    const sellerGstin = String(payload.seller_details?.seller_gstin || '').trim();
    const buyerName = (payload.buyer_details?.buyer_name || '').trim();
    const buyerAddress = String(payload.buyer_details?.buyer_address || '').trim();
    const buyerState = String(payload.buyer_details?.buyer_state || '').trim();
    const buyerGstin = String(payload.buyer_details?.buyer_gstin || '').trim();
    const buyerPan = String(payload.buyer_details?.buyer_pan || '').trim();
    const placeOfSupply = String(payload.buyer_details?.place_of_supply || '').trim();
    const invoiceNumber = (invoice.invoice_number || '').trim();
    const invoiceDate = this.toIsoDateInput(invoice.invoice_date || null);
    const inferredBillDate = this.toIsoDateInput(invoice.delivery_date || invoice.trade_date || invoice.invoice_date || null);
    const totalAmount = this.toNumberOrNull(payload.totals?.total_amount ?? null);
    const itemQuantity = this.firstItemQuantity(payload.item_details || []);
    const inferredRate = totalAmount !== null && itemQuantity !== null && itemQuantity > 0 ? Number((totalAmount / itemQuantity).toFixed(4)) : null;

    if (!this.isVendor()) {
      const sellerId =
        String(matchedContext.seller_vendor_id || '').trim() ||
        this.findSellerVendorIdFromExtracted({
          seller_name: sellerName,
          seller_cin: sellerCin,
          seller_email: sellerEmail,
          seller_state: sellerState,
          seller_gstin: sellerGstin,
          seller_address: sellerAddress,
        });
      if (sellerId && sellerId !== this.sellerVendorId) {
        this.sellerVendorId = sellerId;
        this.onSellerChange();
      }
    }

    const buyerId =
      String(matchedContext.buyer_vendor_id || '').trim() ||
      this.findBuyerVendorIdFromExtracted({
        buyer_name: buyerName,
        buyer_state: buyerState,
        buyer_gstin: buyerGstin,
        buyer_pan: buyerPan,
        buyer_address: buyerAddress,
        place_of_supply: placeOfSupply,
      });
    if (buyerId && buyerId !== this.buyerVendorId) {
      this.buyerVendorId = buyerId;
      this.onBuyerChange();
    }

    const matchedContractId = String(matchedContext.contract_id || '').trim();
    if (matchedContractId && this.availableContracts().some((contract) => String(contract.id) === matchedContractId)) {
      this.contractId = matchedContractId;
      this.onContractChange(matchedContractId);
    }

    const matchedLoaId = String(matchedContext.loa_id || '').trim();
    if (matchedLoaId && this.availableLoas().some((loa) => String(loa.id) === matchedLoaId)) {
      this.onLoaChange(matchedLoaId);
    }

    this.autoSelectBillContextFromExtracted();

    if (invoiceNumber) this.invoiceNumber = invoiceNumber;
    if (invoiceDate) this.invoiceDate = invoiceDate;

    if (this.editingId()) {
      if (inferredBillDate) this.billDate = inferredBillDate;
      if (itemQuantity !== null) this.billedQuantum = itemQuantity;
      if (inferredRate !== null) this.submittedRate = inferredRate;
      if (totalAmount !== null) this.submittedAmount = totalAmount;
    } else {
      this.billEntries.update((rows) => {
        const first = rows[0] ? { ...rows[0] } : this.createEmptyBillEntry();
        if (inferredBillDate) {
          first.billDate = first.billDate || inferredBillDate;
          first.billPeriodFrom = first.billPeriodFrom || inferredBillDate;
          first.billPeriodTo = first.billPeriodTo || inferredBillDate;
        }
        if (itemQuantity !== null) first.billedQuantum = itemQuantity;
        if (inferredRate !== null) first.submittedRate = inferredRate;
        if (totalAmount !== null) first.submittedAmount = totalAmount;
        return [first, ...rows.slice(1)];
      });
    }

    const declaration = (payload.declaration || '').trim();
    if (declaration && !this.remarks.trim()) {
      this.remarks = declaration;
    }

    this.toast.show('Invoice parsed', 'Invoice fields were auto-filled from uploaded file.', 'success');
  }

  private resetFormAfterSubmit(): void {
    this.editingId.set(null);
    this.showForm.set(false);
    this.buyerVendorId = '';
    this.contractId = '';
    this.loaId = '';
    this.invoiceNumber = '';
    this.invoiceDate = '';
    this.billDate = '';
    this.billPeriodFrom = '';
    this.billPeriodTo = '';
    this.billedQuantum = '';
    this.submittedRate = '';
    this.submittedAmount = '';
    this.billEntries.set([this.createEmptyBillEntry()]);
    this.remarks = '';
    this.selectedFile = null;
  }

  private buildCreateFormData(): FormData {
    const formData = new FormData();
    if (this.sellerVendorId) formData.append('seller_vendor_id', this.sellerVendorId);
    formData.append('loa_id', this.loaId);
    formData.append('bill_type', this.billType);
    formData.append('invoice_number', this.invoiceNumber.trim());
    if (this.invoiceDate) formData.append('invoice_date', this.invoiceDate);
    formData.append(
      'bill_entries_json',
      JSON.stringify(
        this.billEntries().map((entry) => {
          const resolvedBillCycle = this.selectedBillCycleForDate(entry.billDate);
          const isDailyMultiLine = this.billEntries().length > 1 && resolvedBillCycle === 'daily';
          const normalizedPeriodFrom = isDailyMultiLine ? entry.billDate || null : entry.billPeriodFrom || null;
          const normalizedPeriodTo = isDailyMultiLine ? entry.billDate || null : entry.billPeriodTo || null;

          return {
            bill_type: this.billType,
            bill_date: entry.billDate,
            bill_period_from: normalizedPeriodFrom,
            bill_period_to: normalizedPeriodTo,
            billed_quantum: this.toNumberOrNull(entry.billedQuantum),
            submitted_rate: this.toNumberOrNull(entry.submittedRate),
            submitted_amount: this.toNumberOrNull(entry.submittedAmount),
          };
        }),
      ),
    );
    if (this.remarks.trim()) formData.append('remarks', this.remarks.trim());
    if (this.selectedFile) formData.append('invoice_file', this.selectedFile);
    return formData;
  }

  private buildEditFormData(): FormData {
    const formData = new FormData();
    if (this.sellerVendorId) formData.append('seller_vendor_id', this.sellerVendorId);
    formData.append('loa_id', this.loaId);
    formData.append('bill_type', this.billType);
    formData.append('invoice_number', this.invoiceNumber.trim());
    if (this.invoiceDate) formData.append('invoice_date', this.invoiceDate);
    formData.append('bill_date', this.billDate);
    if (this.billPeriodFrom) formData.append('bill_period_from', this.billPeriodFrom);
    if (this.billPeriodTo) formData.append('bill_period_to', this.billPeriodTo);
    const billedQuantum = this.toNumberOrNull(this.billedQuantum);
    const rate = this.toNumberOrNull(this.submittedRate);
    const amount = this.toNumberOrNull(this.submittedAmount);
    if (billedQuantum !== null) formData.append('billed_quantum', String(billedQuantum));
    if (rate !== null) formData.append('submitted_rate', String(rate));
    if (amount !== null) formData.append('submitted_amount', String(amount));
    if (this.remarks.trim()) formData.append('remarks', this.remarks.trim());
    if (this.selectedFile) formData.append('invoice_file', this.selectedFile);
    return formData;
  }

  private toNumberOrNull(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private scrollToInvoiceSection(): void {
    setTimeout(() => {
      this.invoiceFormSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  private createEmptyBillEntry(): BillEntryForm {
    return {
      billDate: '',
      billPeriodFrom: '',
      billPeriodTo: '',
      billedQuantum: '',
      submittedRate: '',
      submittedAmount: '',
    };
  }

  private calculateSubmittedAmount(
    billedQuantum: string | number | null | undefined,
    submittedRate: string | number | null | undefined,
  ): string | number {
    const quantum = this.toNumberOrNull(billedQuantum);
    const rate = this.toNumberOrNull(submittedRate);
    if (quantum === null || rate === null) return '';
    return Number((quantum * rate).toFixed(2));
  }

  private findVendorIdByName(name: string): string | null {
    const wanted = this.normalizeLookupName(name);
    if (!wanted) return null;
    const exact = this.vendors().find((vendor) => this.normalizeLookupName(vendor.vendor_name) === wanted);
    if (exact) return String(exact.id);
    const partial = this.vendors().find((vendor) => {
      const source = this.normalizeLookupName(vendor.vendor_name);
      return source.includes(wanted) || wanted.includes(source);
    });
    return partial ? String(partial.id) : null;
  }

  private findSellerVendorIdFromExtracted(seller: {
    seller_name?: string | null;
    seller_cin?: string | null;
    seller_email?: string | null;
    seller_state?: string | null;
    seller_gstin?: string | null;
    seller_address?: string | null;
  }): string | null {
    const normalized = {
      name: this.normalizeLookupName(seller.seller_name),
      cin: this.normalizeTaxId(seller.seller_cin),
      email: this.normalizeEmail(seller.seller_email),
      state: this.normalizeLookupName(seller.seller_state),
      gstin: this.normalizeTaxId(seller.seller_gstin),
      address: this.normalizeAddress(seller.seller_address),
    };

    const checks = [
      { value: normalized.name, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name },
      { value: normalized.cin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.cin) === normalized.cin },
      { value: normalized.email, matches: (vendor: Vendor) => this.normalizeEmail(vendor.email) === normalized.email },
      { value: normalized.state, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.state) === normalized.state },
      { value: normalized.gstin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.gst_number) === normalized.gstin },
      {
        value: normalized.address,
        matches: (vendor: Vendor) =>
          this.normalizeAddress(vendor.billing_address) === normalized.address ||
          this.normalizeAddress(vendor.registered_address) === normalized.address,
      },
    ].filter((check) => !!check.value);

    if (!checks.length) return null;

    const strict = this.vendors().filter((vendor) => checks.every((check) => check.matches(vendor)));
    if (strict.length === 1) return String(strict[0].id);
    if (strict.length > 1) {
      const byName = strict.find((vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name);
      return String((byName || strict[0]).id);
    }

    if (normalized.gstin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.gst_number) === normalized.gstin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.cin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.cin) === normalized.cin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.email) {
      const vendor = this.vendors().find((row) => this.normalizeEmail(row.email) === normalized.email);
      if (vendor) return String(vendor.id);
    }
    if (normalized.name) {
      return this.findVendorIdByName(seller.seller_name || '');
    }
    return null;
  }

  private findBuyerVendorIdFromExtracted(buyer: {
    buyer_name?: string | null;
    buyer_state?: string | null;
    buyer_gstin?: string | null;
    buyer_pan?: string | null;
    buyer_address?: string | null;
    place_of_supply?: string | null;
  }): string | null {
    const normalized = {
      name: this.normalizeLookupName(buyer.buyer_name),
      state: this.normalizeLookupName(buyer.buyer_state || buyer.place_of_supply),
      gstin: this.normalizeTaxId(buyer.buyer_gstin),
      pan: this.normalizeTaxId(buyer.buyer_pan),
      address: this.normalizeAddress(buyer.buyer_address),
    };

    const checks = [
      { value: normalized.name, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name },
      { value: normalized.state, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.state) === normalized.state },
      { value: normalized.gstin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.gst_number) === normalized.gstin },
      { value: normalized.pan, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.pan_number) === normalized.pan },
      {
        value: normalized.address,
        matches: (vendor: Vendor) =>
          this.normalizeAddress(vendor.billing_address) === normalized.address ||
          this.normalizeAddress(vendor.registered_address) === normalized.address,
      },
    ].filter((check) => !!check.value);

    if (!checks.length) return null;

    const strict = this.vendors().filter((vendor) => checks.every((check) => check.matches(vendor)));
    if (strict.length === 1) return String(strict[0].id);
    if (strict.length > 1) {
      const byName = strict.find((vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name);
      return String((byName || strict[0]).id);
    }

    if (normalized.gstin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.gst_number) === normalized.gstin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.pan) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.pan_number) === normalized.pan);
      if (vendor) return String(vendor.id);
    }
    if (normalized.name) {
      return this.findVendorIdByName(buyer.buyer_name || '');
    }
    return null;
  }

  private normalizeLookupName(value: string | null | undefined): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private normalizeTaxId(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  private normalizeEmail(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  private normalizeAddress(value: string | null | undefined): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private firstItemQuantity(items: ExtractedInvoiceItem[]): number | null {
    for (const item of items) {
      const raw = String(item.quantity ?? '').trim();
      if (!raw) continue;
      const match = raw.match(/-?\d+(\.\d+)?/);
      if (!match) continue;
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  private toIsoDateInput(value: string | null | undefined): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const ddmmyyyy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
    const dmy2 = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{2})$/);
    if (dmy2) return `20${dmy2[3]}-${dmy2[2]}-${dmy2[1]}`;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private syncContractFilters(): void {
    const availableBuyerIds = new Set(this.availableBuyers().map((vendor) => String(vendor.id)));
    if (this.buyerVendorId && !availableBuyerIds.has(this.buyerVendorId)) {
      this.buyerVendorId = '';
    }

    const availableContractIds = new Set(this.availableContracts().map((contract) => String(contract.id)));
    if (this.contractId && !availableContractIds.has(this.contractId)) {
      this.contractId = '';
    }

    const availableLoaIds = new Set(this.availableLoas().map((loa) => String(loa.id)));
    if (this.loaId && !availableLoaIds.has(this.loaId)) {
      this.loaId = '';
    }
  }

  private autoSelectBillContextFromExtracted(): void {
    this.syncContractFilters();

    const contracts = this.availableContracts();
    if (!this.contractId && contracts.length === 1) {
      this.contractId = String(contracts[0].id);
    }

    const loas = this.availableLoas();
    if (!this.loaId && loas.length === 1) {
      this.loaId = String(loas[0].id);
      return;
    }

    if (!this.loaId && !this.contractId && loas.length === 1) {
      this.loaId = String(loas[0].id);
    }
  }

  private selectedContract(): Contract | null {
    return this.contracts().find((contract) => String(contract.id) === String(this.contractId || '').trim()) || null;
  }

  private contractSellerLoaIds(contract: Contract | null): string[] {
    if (!contract) return [];
    return (contract.party_details || [])
      .filter((party) => this.contractPartyRole(party.loa_id, party.party_type) === 'seller')
      .map((party) => String(party.loa_id || '').trim())
      .filter((value) => !!value);
  }

  private contractIncludesVendor(contract: Contract, vendorId: string, role: 'buyer' | 'seller'): boolean {
    const normalizedVendorId = String(vendorId || '').trim();
    if (!normalizedVendorId) return false;
    return (contract.party_details || []).some((party) => {
      if (String(party.vendor_id || '').trim() !== normalizedVendorId) return false;
      return this.contractPartyRole(party.loa_id, party.party_type) === role;
    });
  }

  private contractPartyVendorIds(contract: Contract, role: 'buyer' | 'seller'): string[] {
    return (contract.party_details || [])
      .filter((party) => this.contractPartyRole(party.loa_id, party.party_type) === role)
      .map((party) => String(party.vendor_id || '').trim())
      .filter((value) => !!value);
  }

  private contractPartyRole(
    loaId: string | null | undefined,
    partyType: string | null | undefined,
  ): 'buyer' | 'seller' | 'bank' | 'unknown' {
    const loa = this.loas().find((item) => String(item.id) === String(loaId || '').trim()) || null;
    const loaKind = this.resolveLoaKind(loa);
    if (loaKind === 'sell') return 'seller';
    if (loaKind === 'buy') return 'buyer';
    if (loaKind === 'bank') return 'bank';

    const normalizedPartyType = String(partyType || '').trim().toLowerCase();
    if (normalizedPartyType.includes('sell')) return 'seller';
    if (normalizedPartyType.includes('buy')) return 'buyer';
    if (normalizedPartyType.includes('bank')) return 'bank';
    return 'unknown';
  }

  private resolveLoaKind(loa: Loa | null | undefined): 'buy' | 'sell' | 'bank' | 'unknown' {
    const category = String(loa?.category || '').trim().toLowerCase();
    if (category.includes('sell') || category.includes('sale')) return 'sell';
    if (category.includes('buy') || category.includes('purchase')) return 'buy';
    if (category.includes('bank')) return 'bank';
    return 'unknown';
  }

  private selectedBillCycleForDate(billDate: string | null | undefined): string | null {
    const targetDate = this.parseDateValue(billDate);
    const configs = (this.billConfigsByLoa()[this.loaId] || [])
      .filter((item) => (item.bill_type || '').trim().toUpperCase() === this.billType)
      .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''));

    if (!configs.length) return null;
    if (!targetDate) return (configs[0].bill_cycle || '').trim().toLowerCase() || null;

    const matched = configs.find((config) => {
      const effectiveDate = this.parseDateValue(config.effective_date);
      return !effectiveDate || effectiveDate.getTime() <= targetDate.getTime();
    });
    return ((matched || configs[0]).bill_cycle || '').trim().toLowerCase() || null;
  }

  private supportedBillConfigs(configs: LoaBillConfig[]): LoaBillConfig[] {
    return (configs || [])
      .filter((item) => {
        const billType = (item.bill_type || '').trim().toUpperCase();
        return billType === 'SOP' || billType === 'TMR';
      })
      .sort((left, right) => {
        const leftDate = left.effective_date || '';
        const rightDate = right.effective_date || '';
        if (left.bill_type !== right.bill_type) return left.bill_type.localeCompare(right.bill_type);
        return rightDate.localeCompare(leftDate);
      })
      .filter((item, index, rows) => index === rows.findIndex((row) => (row.bill_type || '').trim().toUpperCase() === (item.bill_type || '').trim().toUpperCase()));
  }

  private matchingVerification(
    verifications: BillVerification[],
    loaId: string,
    billType: BillTypeOption,
    billCycle: string,
    periodFrom: Date,
    periodTo: Date,
    rowTotalQuantum: number,
  ): BillVerification | null {
    const matches = verifications
      .map((row) => {
        if (row.loa_id !== loaId) return null;
        if ((row.bill_type || '').trim().toUpperCase() !== billType) return null;

        const range = this.resolveVerificationRange(row, billCycle, rowTotalQuantum);
        if (!range) return null;
        const matchKind = this.verificationRangeMatchKind(periodFrom, periodTo, range.from, range.to);
        if (matchKind === 0) return null;

        return { row, matchKind, specificity: range.specificity, spanDays: range.spanDays };
      })
      .filter(
        (
          item,
        ): item is {
          row: BillVerification;
          matchKind: number;
          specificity: number;
          spanDays: number;
        } => !!item,
      );

    if (!matches.length) return null;
    matches.sort((left, right) => {
      if (right.matchKind !== left.matchKind) return right.matchKind - left.matchKind;
      if (right.specificity !== left.specificity) return right.specificity - left.specificity;
      if (left.spanDays !== right.spanDays) return left.spanDays - right.spanDays;
      return (right.row.created_at || '').localeCompare(left.row.created_at || '');
    });
    return matches[0].row;
  }

  private rangesExactlyMatch(fromA: Date, toA: Date, fromB: Date, toB: Date): boolean {
    return this.floorDate(fromA).getTime() === this.floorDate(fromB).getTime() && this.floorDate(toA).getTime() === this.floorDate(toB).getTime();
  }

  private verificationRangeMatchKind(periodFrom: Date, periodTo: Date, verificationFrom: Date, verificationTo: Date): number {
    if (this.rangesExactlyMatch(periodFrom, periodTo, verificationFrom, verificationTo)) return 2;
    if (this.rangeContains(verificationFrom, verificationTo, periodFrom, periodTo)) return 1;
    return 0;
  }

  private rangeContains(containerFrom: Date, containerTo: Date, innerFrom: Date, innerTo: Date): boolean {
    return this.floorDate(containerFrom).getTime() <= this.floorDate(innerFrom).getTime() &&
      this.floorDate(containerTo).getTime() >= this.floorDate(innerTo).getTime();
  }

  private resolveVerificationRange(
    row: BillVerification,
    billCycle: string,
    rowTotalQuantum: number,
  ): { from: Date; to: Date; specificity: number; spanDays: number } | null {
    const explicitFrom = this.parseDateValue(row.bill_period_from);
    const explicitTo = this.parseDateValue(row.bill_period_to);
    if (explicitFrom && explicitTo) {
      const from = explicitFrom.getTime() <= explicitTo.getTime() ? explicitFrom : explicitTo;
      const to = explicitFrom.getTime() <= explicitTo.getTime() ? explicitTo : explicitFrom;
      return {
        from,
        to,
        specificity: 3,
        spanDays: this.rangeDayCount(from, to),
      };
    }

    const billDate = this.parseDateValue(row.bill_date);
    if (!billDate) return null;

    const normalizedCycle = (billCycle || '').trim().toLowerCase();
    if (normalizedCycle.startsWith('daily')) {
      const billedQuantum = Number(row.billed_quantum);
      const inferredDays =
        rowTotalQuantum > 0 && Number.isFinite(billedQuantum) && billedQuantum > rowTotalQuantum
          ? Math.max(1, Math.round(billedQuantum / rowTotalQuantum))
          : 1;
      const from = new Date(billDate.getFullYear(), billDate.getMonth(), billDate.getDate() - (inferredDays - 1));
      return {
        from,
        to: billDate,
        specificity: inferredDays > 1 ? 2 : 1,
        spanDays: inferredDays,
      };
    }

    return {
      from: billDate,
      to: billDate,
      specificity: 1,
      spanDays: 1,
    };
  }

  private rangeDayCount(from: Date, to: Date): number {
    return Math.max(1, Math.floor((this.floorDate(to).getTime() - this.floorDate(from).getTime()) / 86400000) + 1);
  }

  private floorDate(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private applicationComboLabel(applicationNumbers: string[]): { label: string; tooltip: string } {
    const numbers = applicationNumbers || [];
    if (!numbers.length) {
      return { label: '-', tooltip: '-' };
    }

    const tooltip = numbers.join(', ');
    const limit = 3;
    const shown = numbers.slice(0, limit);
    const remaining = numbers.length - shown.length;
    const label = remaining > 0 ? `${shown.join(', ')} +${remaining} more` : shown.join(', ');
    return { label, tooltip };
  }

  private loaScheduleBounds(apps: ApplicationApi[]): { start: Date; end: Date } | null {
    const parsed = apps
      .flatMap((app) => this.approvedScheduleDates(app))
      .map((item) => this.parseDateValue(item))
      .filter((item): item is Date => !!item)
      .sort((left, right) => left.getTime() - right.getTime());

    if (!parsed.length) return null;

    const start = this.floorDate(parsed[0]);
    const end = this.floorDate(parsed[parsed.length - 1]);
    if (end.getTime() < start.getTime()) return null;
    return { start, end };
  }

  private applicationDateBounds(app: ApplicationApi): { start: Date; end: Date } | null {
    const parsed = this.approvedScheduleDates(app)
      .map((item) => this.parseDateValue(item))
      .filter((item): item is Date => !!item)
      .sort((left, right) => left.getTime() - right.getTime());

    if (!parsed.length) return null;
    return { start: this.floorDate(parsed[0]), end: this.floorDate(parsed[parsed.length - 1]) };
  }

  private billPeriodsForCycle(
    scheduleStart: Date,
    scheduleEnd: Date,
    billCycle: string,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const cycle = (billCycle || '').trim().toLowerCase();

    if (cycle.startsWith('monthly')) {
      return this.monthlyBillPeriods(scheduleStart, scheduleEnd);
    }
    if (cycle.startsWith('weekly')) {
      return this.weeklyBillPeriods(scheduleStart, scheduleEnd);
    }
    if (cycle.startsWith('daily')) {
      return this.dailyBillPeriods(scheduleStart, scheduleEnd);
    }

    if (scheduleEnd.getTime() < scheduleStart.getTime()) return [];
    return [{ from: scheduleStart, to: scheduleEnd, billDate: scheduleEnd }];
  }

  private dailyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const start = this.floorDate(scheduleStart);
    const end = this.floorDate(scheduleEnd);
    if (end.getTime() < start.getTime()) return [];

    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (cursor.getTime() <= end.getTime()) {
      const day = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
      periods.push({ from: day, to: day, billDate: day });
      cursor.setDate(cursor.getDate() + 1);
    }
    return periods;
  }

  private monthlyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    let cursor = new Date(scheduleStart.getFullYear(), scheduleStart.getMonth(), 1);
    const endMonth = new Date(scheduleEnd.getFullYear(), scheduleEnd.getMonth(), 1);

    while (cursor.getTime() <= endMonth.getTime()) {
      const monthFrom = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthTo = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      if (this.rangesOverlap(monthFrom, monthTo, scheduleStart, scheduleEnd)) {
        periods.push({ from: monthFrom, to: monthTo, billDate: monthTo });
      }
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return periods;
  }

  private weeklyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    let cursor = new Date(scheduleStart.getFullYear(), scheduleStart.getMonth(), 1);
    const endMonth = new Date(scheduleEnd.getFullYear(), scheduleEnd.getMonth(), 1);

    while (cursor.getTime() <= endMonth.getTime()) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const lastDay = new Date(year, month + 1, 0).getDate();

      const buckets: Array<{ startDay: number; endDay: number }> = [
        { startDay: 1, endDay: 7 },
        { startDay: 8, endDay: 15 },
        { startDay: 16, endDay: 23 },
        { startDay: 24, endDay: lastDay },
      ];

      buckets.forEach((bucket) => {
        const from = new Date(year, month, bucket.startDay);
        const to = new Date(year, month, bucket.endDay);
        if (!this.rangesOverlap(from, to, scheduleStart, scheduleEnd)) return;
        periods.push({ from, to, billDate: to });
      });

      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return periods;
  }

  private rangesOverlap(fromA: Date, toA: Date, fromB: Date, toB: Date): boolean {
    return fromA.getTime() <= toB.getTime() && fromB.getTime() <= toA.getTime();
  }

  private billCycleBucket(
    billCycle: string,
    anchorDateYmd: string,
  ): { bucketKey: string; bucketLabel: string; bucketFrom: string; bucketTo: string } {
    const cycle = (billCycle || '').trim().toLowerCase();
    const parsedAnchor = this.parseDateValue(anchorDateYmd) ?? new Date();
    const year = parsedAnchor.getFullYear();
    const month = parsedAnchor.getMonth() + 1;
    const day = parsedAnchor.getDate();
    const lastDay = this.daysInMonth(year, month);

    if (cycle.startsWith('monthly')) {
      const from = `${year}-${String(month).padStart(2, '0')}-01`;
      const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return {
        bucketKey: `${year}-${String(month).padStart(2, '0')}`,
        bucketLabel: `${this.monthLabel(year, month)} (1-${lastDay})`,
        bucketFrom: from,
        bucketTo: to,
      };
    }

    if (cycle.startsWith('weekly')) {
      const { startDay, endDay } =
        day <= 7
          ? { startDay: 1, endDay: 7 }
          : day <= 15
            ? { startDay: 8, endDay: 15 }
            : day <= 23
              ? { startDay: 16, endDay: 23 }
              : { startDay: 24, endDay: lastDay };

      const from = `${year}-${String(month).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
      const to = `${year}-${String(month).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
      return {
        bucketKey: `${year}-${String(month).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`,
        bucketLabel: `${this.monthLabel(year, month)} (${startDay}-${endDay})`,
        bucketFrom: from,
        bucketTo: to,
      };
    }

    if (cycle.startsWith('daily')) {
      const value = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return {
        bucketKey: value,
        bucketLabel: value,
        bucketFrom: value,
        bucketTo: value,
      };
    }

    const value = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return {
      bucketKey: value,
      bucketLabel: value,
      bucketFrom: value,
      bucketTo: value,
    };
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
  }

  private monthLabel(year: number, month: number): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const name = monthNames[month - 1] || String(month);
    return `${name} ${year}`;
  }

  private billRateLabel(config: LoaBillConfig): string {
    const billType = (config.bill_type || '').trim().toUpperCase();
    const rateType = (config.rate_type || '').trim().toUpperCase();
    if (billType === 'TMR') {
      return this.valueOrDash(config.tmr_rate ?? config.rate);
    }
    if (rateType === 'MCP') {
      const base = this.valueOrDash(config.mcp_rate ?? config.rate);
      const condition = (config.mcp_condition || '').trim();
      return condition ? `${condition} ${base}`.trim() : base;
    }
    return this.valueOrDash(config.rate ?? config.mcp_rate);
  }

  private billRateValue(config: LoaBillConfig): number | null {
    const billType = (config.bill_type || '').trim().toUpperCase();
    const rateType = (config.rate_type || '').trim().toUpperCase();
    const candidate =
      billType === 'TMR'
        ? config.tmr_rate ?? config.rate
        : rateType === 'MCP'
          ? config.mcp_rate ?? config.rate
          : config.rate ?? config.mcp_rate;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private applicationQuantumForRange(app: ApplicationApi, from: Date, to: Date): number {
    return (app.approval_schedules || [])
      .filter((schedule) => schedule.is_approved === true)
      .reduce((sum, schedule) => {
        return sum + this.approvedScheduleQuantumForRange(schedule, from, to);
      }, 0);
  }

  private approvedScheduleQuantumForRange(schedule: ApplicationApprovalScheduleApi, from: Date, to: Date): number {
    const scheduleFrom = this.parseDateValue(schedule.schedule_from);
    const scheduleTo = this.parseDateValue(schedule.schedule_to || schedule.schedule_from) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.start_time, schedule.end_time);
    const approvedQuantum = this.approvedScheduleQuantum(schedule);
    if (approvedQuantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * approvedQuantum;
  }

  private applicationHasApprovedSchedule(app: ApplicationApi): boolean {
    return (app.approval_schedules || []).some((schedule) => schedule.is_approved === true);
  }

  private approvedScheduleDates(app: ApplicationApi): string[] {
    return (app.approval_schedules || [])
      .filter((item) => item.is_approved === true)
      .flatMap((item) => [item.schedule_from, item.schedule_to || item.schedule_from]);
  }

  private applicationAmountForRange(apps: ApplicationApi[], from: Date, to: Date, rate: number): number {
    return apps.reduce((sum, app) => {
      return (
        sum +
        (app.approval_schedules || [])
          .filter((schedule) => schedule.is_approved === true)
          .reduce((scheduleSum, schedule) => scheduleSum + this.approvedScheduleAmountForRange(schedule, from, to, rate), 0)
      );
    }, 0);
  }

  private approvedScheduleAmountForRange(schedule: ApplicationApprovalScheduleApi, from: Date, to: Date, rate: number): number {
    const scheduleFrom = this.parseDateValue(schedule.schedule_from);
    const scheduleTo = this.parseDateValue(schedule.schedule_to || schedule.schedule_from) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.start_time, schedule.end_time);
    const approvedQuantum = this.approvedScheduleQuantum(schedule);
    if (approvedQuantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * approvedQuantum * rate;
  }

  private approvedScheduleQuantum(schedule: ApplicationApprovalScheduleApi): number {
    const acceptedMw = Number(schedule.accepted_mw);
    if (Number.isFinite(acceptedMw) && acceptedMw > 0) return acceptedMw;

    const requestedMw = Number(schedule.requested_mw);
    if (Number.isFinite(requestedMw) && requestedMw > 0) return requestedMw;

    const mwh = Number(schedule.mwh);
    return Number.isFinite(mwh) && mwh > 0 ? mwh : 0;
  }

  private scheduleQuantumForRange(schedule: ApplicationScheduleApi, from: Date, to: Date): number {
    const scheduleFrom = this.parseDateValue(schedule.from_date);
    const scheduleTo = this.parseDateValue(schedule.to_date || schedule.from_date) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.from_time, schedule.to_time);
    const quantum = Number(schedule.quantum);
    if (!Number.isFinite(quantum) || quantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * quantum;
  }

  private scheduleHourDifference(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const startMinutes = this.timeToMinutes(fromTime, false);
    const endMinutes = this.timeToMinutes(toTime, true);
    if (startMinutes === null || endMinutes === null) return 0;
    if (endMinutes <= startMinutes) return 0;
    return (endMinutes - startMinutes) / 60;
  }

  private timeToMinutes(value: string | null | undefined, allow24Hour = false): number | null {
    const raw = (value || '').trim();
    if (!raw) return allow24Hour ? 1440 : 0;
    const [hourRaw, minuteRaw] = raw.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (allow24Hour && hour === 24 && minute === 0) return 1440;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return hour * 60 + minute;
  }

  private valueOrDash(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  private parseDateValue(value: string | null | undefined): Date | null {
    const raw = (value || '').trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const parsed = new Date(`${raw}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('/');
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('-');
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
